/**
 * TravelSystem — Core travel logic, decoupled from rendering.
 *
 * Tracks current region, position, day count, time of day.
 * Emits events via EventBus for UI and rendering to react to.
 *
 * @module TravelSystem
 */

import eventBus from '../core/EventBus.js';
import dataStore from './DataLoader.js';

/** Number of nodes per day. */
const NODES_PER_DAY = 5;

/** Default seconds of real-time per node at normal speed. */
const SECONDS_PER_NODE = 3;

/** Default encounter chance per node (0-1). */
const DEFAULT_ENCOUNTER_CHANCE = 0.30;

/** Time-of-day periods mapped to node index within a day. */
const TIME_PERIODS = ['Morning', 'Afternoon', 'Afternoon', 'Evening', 'Night'];

/**
 * Day/night tint configurations.
 * Each entry: { color: hex number, alpha: 0-1 }
 */
const TINT_MAP = {
  Morning:   { color: 0xffcc66, alpha: 0.10 },
  Afternoon: { color: 0xffffff, alpha: 0.00 },
  Evening:   { color: 0xff8833, alpha: 0.18 },
  Night:     { color: 0x112244, alpha: 0.40 },
};

class TravelSystem {
  constructor() {
    /** @type {string|null} Current region ID */
    this.currentRegionId = null;

    /** @type {object|null} Cached region data */
    this.currentRegion = null;

    /** Position along the current region path (0 to regionLength). */
    this.position = 0;

    /** Total travel distance accumulated within the current node. */
    this.nodeProgress = 0;

    /** Current node index within the current day (0 to NODES_PER_DAY - 1). */
    this.nodeInDay = 0;

    /** Day counter (starts at 1). */
    this.day = 1;

    /** Total nodes traveled across the entire journey. */
    this.totalNodes = 0;

    /** Whether the caravan is currently moving. */
    this.traveling = false;

    /** Whether travel is paused (encounter, dialog, etc.). */
    this.paused = false;

    /** Speed multiplier (1 = normal, 2 = fast). */
    this.speedMultiplier = 1;

    /** Encounter chance per node. */
    this.encounterChance = DEFAULT_ENCOUNTER_CHANCE;

    /** Reference to the party, set externally. */
    this.party = null;

    /** Whether the caravan has reached the region edge. */
    this.atRegionEdge = false;

    /** Direction of travel: 'right' or 'left'. */
    this.direction = 'right';
  }

  /**
   * Initialize the travel system with a starting region.
   * @param {string} regionId
   * @param {object} [party] - Party reference
   */
  init(regionId, party = null) {
    this.party = party;
    this.changeRegion(regionId);
    this.day = 1;
    this.nodeInDay = 0;
    this.totalNodes = 0;
    this.traveling = true;
    this.paused = false;
    this.atRegionEdge = false;
  }

  /**
   * Change to a new region.
   * @param {string} regionId
   */
  changeRegion(regionId) {
    const region = dataStore.getById('regions', regionId);
    if (!region) {
      console.error(`[TravelSystem] Region not found: ${regionId}`);
      return;
    }

    const oldRegion = this.currentRegionId;
    this.currentRegionId = regionId;
    this.currentRegion = region;
    this.position = 0;
    this.nodeProgress = 0;
    this.atRegionEdge = false;

    eventBus.emit('travel:region-change', {
      from: oldRegion,
      to: regionId,
      region,
    });
  }

  /**
   * Advance the caravan forward by dt seconds.
   * Returns true if a node was completed this frame.
   * @param {number} dt - Delta time in seconds
   * @returns {boolean}
   */
  advance(dt) {
    if (!this.traveling || this.paused) return false;

    const speed = this.speedMultiplier;
    this.nodeProgress += (dt * speed) / SECONDS_PER_NODE;
    this.position += (dt * speed) / SECONDS_PER_NODE;

    // Check if we completed a node
    if (this.nodeProgress >= 1) {
      this.nodeProgress -= 1;
      this.totalNodes++;
      this.nodeInDay++;

      eventBus.emit('travel:node', {
        totalNodes: this.totalNodes,
        nodeInDay: this.nodeInDay,
        day: this.day,
        timeOfDay: this.getTimeOfDay(),
      });

      // Check for day advancement
      if (this.nodeInDay >= NODES_PER_DAY) {
        this.nodeInDay = 0;
        this.day++;
        eventBus.emit('travel:day-advance', {
          day: this.day,
        });
      }

      // Check for encounter
      if (this._rollEncounter()) {
        this.paused = true;
        const encounter = this._generateEncounter();
        eventBus.emit('travel:encounter', encounter);
        return true;
      }

      // Check for region edge (after ~10 nodes in the region)
      if (this.position >= 10) {
        this.atRegionEdge = true;
        this.paused = true;
        eventBus.emit('travel:region-edge', {
          region: this.currentRegion,
          connections: this.currentRegion.connections,
        });
        return true;
      }

      return true;
    }

    return false;
  }

  /**
   * Resume travel after an encounter or dialog.
   */
  resume() {
    this.paused = false;
    this.atRegionEdge = false;
  }

  /**
   * Get the current time-of-day string.
   * @returns {string} 'Morning' | 'Afternoon' | 'Evening' | 'Night'
   */
  getTimeOfDay() {
    const idx = Math.min(this.nodeInDay, TIME_PERIODS.length - 1);
    return TIME_PERIODS[idx];
  }

  /**
   * Get the current day/night tint color and alpha.
   * @returns {{ color: number, alpha: number }}
   */
  getDayNightTint() {
    const tod = this.getTimeOfDay();
    return TINT_MAP[tod] || TINT_MAP.Afternoon;
  }

  /**
   * Get the fractional position within the current node (0-1).
   * Used by the renderer to interpolate caravan position.
   * @returns {number}
   */
  getNodeFraction() {
    return this.nodeProgress;
  }

  /**
   * Set the time of day directly (for debug commands).
   * @param {number} nodeIndex - 0 to NODES_PER_DAY-1
   */
  setTimeOfDay(nodeIndex) {
    this.nodeInDay = Math.max(0, Math.min(NODES_PER_DAY - 1, nodeIndex));
    eventBus.emit('travel:time-changed', {
      nodeInDay: this.nodeInDay,
      timeOfDay: this.getTimeOfDay(),
    });
  }

  /**
   * Set the day counter directly (for debug).
   * @param {number} dayNum
   */
  setDay(dayNum) {
    this.day = Math.max(1, dayNum);
  }

  /**
   * Toggle speed between normal and fast.
   * @returns {number} new speed multiplier
   */
  toggleSpeed() {
    this.speedMultiplier = this.speedMultiplier === 1 ? 2 : 1;
    eventBus.emit('travel:speed-changed', { speed: this.speedMultiplier });
    return this.speedMultiplier;
  }

  /**
   * Set speed multiplier directly.
   * @param {number} mult
   */
  setSpeed(mult) {
    this.speedMultiplier = mult;
    eventBus.emit('travel:speed-changed', { speed: this.speedMultiplier });
  }

  // ── Private Methods ──────────────────────────────────

  /**
   * Roll for a random encounter.
   * @returns {boolean}
   */
  _rollEncounter() {
    return Math.random() < this.encounterChance;
  }

  /**
   * Generate encounter data from the current region's enemy list.
   * @returns {object}
   */
  _generateEncounter() {
    const region = this.currentRegion;
    if (!region || !region.enemies || region.enemies.length === 0) {
      return { type: 'empty', enemies: [] };
    }

    // Pick a random enemy from the region
    const enemyIds = region.enemies;
    const randomId = enemyIds[Math.floor(Math.random() * enemyIds.length)];
    const enemyData = dataStore.getById('enemies', randomId);

    if (!enemyData) {
      return { type: 'empty', enemies: [] };
    }

    // Determine count (1-3 for most, 1 for bosses)
    let count = 1;
    if (enemyData.behavior !== 'boss') {
      count = Math.floor(Math.random() * 3) + 1;
    }

    return {
      type: 'combat',
      enemies: Array(count).fill(null).map(() => ({
        id: enemyData.id,
        name: enemyData.name,
        level: region.levelRange[0] + Math.floor(Math.random() * (region.levelRange[1] - region.levelRange[0] + 1)),
      })),
      regionId: region.id,
    };
  }

  /**
   * Force trigger an encounter (for debug commands).
   * @returns {object} encounter data
   */
  triggerEncounter() {
    this.paused = true;
    const encounter = this._generateEncounter();
    eventBus.emit('travel:encounter', encounter);
    return encounter;
  }

  /**
   * Serialize state for saving.
   * @returns {object}
   */
  toJSON() {
    return {
      currentRegionId: this.currentRegionId,
      position: this.position,
      nodeProgress: this.nodeProgress,
      nodeInDay: this.nodeInDay,
      day: this.day,
      totalNodes: this.totalNodes,
      speedMultiplier: this.speedMultiplier,
      encounterChance: this.encounterChance,
    };
  }

  /**
   * Restore state from save data.
   * @param {object} data
   */
  fromJSON(data) {
    if (data.currentRegionId) this.changeRegion(data.currentRegionId);
    this.position = data.position ?? 0;
    this.nodeProgress = data.nodeProgress ?? 0;
    this.nodeInDay = data.nodeInDay ?? 0;
    this.day = data.day ?? 1;
    this.totalNodes = data.totalNodes ?? 0;
    this.speedMultiplier = data.speedMultiplier ?? 1;
    this.encounterChance = data.encounterChance ?? DEFAULT_ENCOUNTER_CHANCE;
    this.traveling = true;
    this.paused = false;
  }
}

// Export as singleton
const travelSystem = new TravelSystem();
export default travelSystem;
export { TravelSystem, NODES_PER_DAY, SECONDS_PER_NODE, TIME_PERIODS, TINT_MAP };
